import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Typography,Box
} from '@mui/material';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { Tooltip } from '@mui/material';



export const AssetDetails = ({ assetData, searchQuery }) => {

  // Sorting state
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const renderWithTooltip = (text, limit = 20) => {

    if (!text) return "N/A";

    const displayText = text.length > limit ? `${text.slice(0, limit)}...` : text;

    return (
      <Tooltip

        title={text}
        arrow
        placement="top"
        PopperProps={{
          container: document.body,
          style: { zIndex: 10000 }
        }}
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "#ece5e5ff",
              color: "black",
              fontSize: "10px",
              fontFamily: "Poppins",
              padding: "8px 10px",
              borderRadius: "6px",
              maxWidth: "400px",
              whiteSpace: "pre-wrap",
            },
          }
        }}>
        <span style={{ display: 'inline-block', cursor: 'pointer' }}>
          {displayText}
        </span>
      </Tooltip>

    );

  };



  // Column search state
  const [columnSearch, setColumnSearch] = useState({
    name: "",
    type: "",
    is_cde: "",
    metadata: "",
    businessterm: "",
    transformationLogic: "",
    sample: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { assetsOverview, tableDetails } = assetData || {};



  // Handle sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  // Handle column search change
  const handleColumnSearchChange = (column, value) => {
    setColumnSearch((prev) => ({
      ...prev,
      [column]: value,
    }));
    // Reset to first page when searching
    setCurrentPage(1);
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    if (!tableDetails || !Array.isArray(tableDetails)) return [];

    let filtered = [...tableDetails];

    // Apply column filters
    Object.entries(columnSearch).forEach(([column, searchValue]) => {
      if (searchValue && searchValue.trim()) {
        filtered = filtered.filter((row) => {
          let cellValue = "";

          // Handle boolean for is_cde
          if (column === "is_cde") {
            cellValue = row[column] ? "yes" : "no";
          } else {
            cellValue = row[column] || "";
          }

          return String(cellValue)
            .toLowerCase()
            .includes(searchValue.toLowerCase().trim());
        });
      }
    });

    // Apply sorting
    if (sortColumn) {
      filtered.sort((a, b) => {
        let aVal = a[sortColumn];
        let bVal = b[sortColumn];

        // Handle boolean for is_cde
        if (sortColumn === "is_cde") {
          aVal = aVal ? "yes" : "no";
          bVal = bVal ? "yes" : "no";
        }

        // Handle null/undefined
        if (aVal === null || aVal === undefined) aVal = "";
        if (bVal === null || bVal === undefined) bVal = "";

        // String comparison
        const comparison = String(aVal)
          .toLowerCase()
          .localeCompare(String(bVal).toLowerCase());

        return sortOrder === "asc" ? comparison : -comparison;
      });
    }

    return filtered;
  }, [tableDetails, columnSearch, sortColumn, sortOrder]);

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(filteredAndSortedData.length / itemsPerPage));
  const validCurrentPage = Math.min(currentPage, totalPages);
  const indexOfFirstItem = (validCurrentPage - 1) * itemsPerPage;
  const indexOfLastItem = Math.min(indexOfFirstItem + itemsPerPage, filteredAndSortedData.length);
  const currentData = filteredAndSortedData.slice(indexOfFirstItem, indexOfLastItem);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Render pagination buttons
  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, validCurrentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    buttons.push(
      <button
        key="prev"
        className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        disabled={validCurrentPage === 1}
        onClick={() => handlePageChange(validCurrentPage - 1)}
      >
        Previous
      </button>
    );

    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 transition-colors"
          onClick={() => handlePageChange(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(
          <span key="ellipsis1" className="px-3 py-2 text-sm text-gray-500">
            ...
          </span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`px-3 py-2 text-sm border rounded-md transition-colors ${validCurrentPage === i
            ? "bg-[#bb1ccc] text-white border-[#bb1ccc] shadow-sm"
            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <span key="ellipsis2" className="px-3 py-2 text-sm text-gray-500">
            ...
          </span>
        );
      }
      buttons.push(
        <button
          key={totalPages}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 transition-colors"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    buttons.push(
      <button
        key="next"
        className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        disabled={validCurrentPage === totalPages}
        onClick={() => handlePageChange(validCurrentPage + 1)}
      >
        Next
      </button>
    );

    return buttons;
  };

  const countriesString = assetsOverview?.country || "";
  const countries = countriesString
    .split(",")
    .map(c => c.trim())
    .filter(Boolean);

  const displayed = countries.slice(0, 2);
  const extraCount = countries.length - 2;


  // Column configuration
  const columns = [
    { key: "name", label: "Column Name" },
    { key: "type", label: "Type" },
    { key: "is_cde", label: "CDE" },
    { key: "metadata", label: "Technical Metadata" },
    { key: "businessterm", label: "Business Description" },
    { key: "transformationLogic", label: "Transformation Logic" },
    { key: "sample", label: "Sample Value" },
  ];

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortColumn === column) {
      return sortOrder === "asc" ? (
        <ArrowUp size={12} />
      ) : (
        <ArrowDown size={12} />
      );
    }
    return <ArrowUp size={12} className="opacity-30" />;
  };

  if (!assetData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No asset data available</p>
      </div>
    );
  }

  // Function to check if a column matches the search term from main page
  const isColumnMatch = (column) => {
    if (!searchQuery || !searchQuery.trim()) return false;

    const searchLower = searchQuery.toLowerCase();
    const searchWithUnderscores = searchLower.replace(/\s+/g, '_');

    const columnName = (column.name || '').toLowerCase();
    const columnType = (column.type || '').toLowerCase();
    const columnMetadata = (column.metadata || '').toLowerCase();
    const columnSample = (column.sample || '').toLowerCase();

    return columnName.includes(searchLower) ||
      columnName.includes(searchWithUnderscores) ||
      columnType.includes(searchLower) ||
      columnType.includes(searchWithUnderscores) ||
      columnMetadata.includes(searchLower) ||
      columnMetadata.includes(searchWithUnderscores) ||
      columnSample.includes(searchLower) ||
      columnSample.includes(searchWithUnderscores);
  };

  return (
    <div className="space-y-6 mb-[2rem]">
     
<div className="flex justify-between items-start mb-3">
<div className="flex-1">
<Typography

      variant="h5"

      sx={{

        color: "#bb1ccc",

        fontWeight: 700,

        fontSize: "1.5rem",

        marginBottom: 1,

        display: "flex",

        alignItems: "center",

        gap: 1,

      }}
>
<Box

        sx={{

          width: "4px",

          height: "24px",

          backgroundColor: "#bb1ccc",

          borderRadius: "2px",

        }}

      />

      {assetsOverview?.data_asset_name?.replace(/\|/g, ".") || "Asset Name"}
</Typography>
 
    <Typography

      variant="body2"

      sx={{

        color: "#000000",

        fontSize: "14px",

        fontStyle: "normal",

        padding: "10px 5px",

      }}
>

      {assetsOverview?.data_assets_description || "No description available"}
</Typography>
</div>
</div>

 

      {/* Table Details Section */}
      <div>
        <div className="flex justify-between items-start mb-2">
          <Typography variant="h6" sx={{
            color: '#bb1ccc',
            fontWeight: 600,
            fontSize: '1.25rem',
            marginBottom: 1
          }}>
            Table Details
          </Typography>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              {/* Table Header with Sort and Search - Same UI as IncidentTableHead */}
              <thead className="bg-[#9897FF1A] text-[#404040] font-normal">
                {/* Header Row with Sort */}
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className="px-4 pb-[6px] pr-[2px] text-[12px] pt-[10px] text-left whitespace-nowrap cursor-pointer select-none font-normal"
                      style={{ width: col.key === 'name' ? '20%' : '20%' }}
                    >
                      <span className="flex items-center gap-1">
                        {col.label}
                        {renderSortIcon(col.key)}
                      </span>
                    </th>
                  ))}
                </tr>

                {/* Search Row */}
                <tr>
                  {columns.map((col) => (
                    <th key={`search-${col.key}`} className="px-4 pt-0 pb-[10px] font-normal">
                      <input
                        className="w-full text-[12px] pb-[5px] pl-[10px] placeholder-gray-400 bg-white rounded py-2 outline-none font-normal placeholder:font-normal"
                        placeholder={col.key === "is_cde" ? "Yes/No" : "Search"}
                        value={columnSearch[col.key] || ""}
                        onChange={(e) => handleColumnSearchChange(col.key, e.target.value)}
                      />
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Table Body */}
              <tbody className="divide-y divide-gray-200 bg-white">
                {currentData.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="py-8 text-center text-gray-500">
                      No data found matching your search criteria
                    </td>
                  </tr>
                ) : (
                  currentData.map((column, index) => (
                    <tr
                      key={column.attribute_id || index}
                      className="hover:bg-gray-50 transition-colors"
                      style={{
                        backgroundColor: isColumnMatch(column) && searchQuery ? '#fff3cd' : (index % 2 === 0 ? '#fafafa' : '#ffffff'),
                        border: isColumnMatch(column) && searchQuery ? '2px solid #ffc107' : 'none'
                      }}
                    >
                      <td className="py-2 px-4 text-[12px]">
                        <div className="font text-gray-900">{column.name}</div>
                      </td>
                      <td className="py-2 px-4 text-[12px] text-gray-600 capitalize">
                        {column.type}
                      </td>
                      <td className="py-2 px-4 text-[12px] text-gray-600">
                        {column.is_cde ? "Yes" : "No"}
                      </td>
                      <td
                        className="py-2 px-4 text-[12px] text-gray-600"
                        style={{ maxWidth: "300px", wordWrap: "break-word" }}
                      >
                        {renderWithTooltip(column.metadata)}
                      </td>
                      <td className="py-2 px-4 text-[12px] text-gray-600" style={{ maxWidth: '300px', wordWrap: 'break-word' }}>
                        {column.businessterm || 'N/A'}
                      </td>
                      <td className="py-2 px-4 text-[12px] text-gray-600" style={{ maxWidth: '200px', wordWrap: 'break-word' }}>
                        {column.transformationLogic || 'N/A'}
                      </td>
                      <td className="py-2 px-4 text-[12px] text-gray-600" style={{ maxWidth: '200px', wordWrap: 'break-word' }}>
                        {/* {column.sample || 'N/A'} */}
                        {renderWithTooltip(column.sample)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 0 && (
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 py-4 px-4 border-t border-gray-200 bg-white">
              <div className="text-sm text-gray-600">
                Showing <span className="font-medium">{filteredAndSortedData.length > 0 ? indexOfFirstItem + 1 : 0}</span> to{" "}
                <span className="font-medium">{indexOfLastItem}</span> of{" "}
                <span className="font-medium">{filteredAndSortedData.length}</span> results
                {tableDetails && filteredAndSortedData.length !== tableDetails.length && (
                  <span className="text-gray-400 ml-2">
                    (filtered from {tableDetails.length} total)
                  </span>
                )}
              </div>
              <div className="flex space-x-1">{renderPaginationButtons()}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetDetails;
